/**
 * DFE (Departure From Estimate) API Contract
 *
 * Endpoints:
 *   GET  /jobs/dfe/:jobId   → list DFE submissions with line items
 *   POST /jobs/dfe/:jobId   → create a new DFE submission with line items
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

/**
 * Zod schema for a DFE line item (product item)
 */
export const dfeLineItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  unit: z.string().nullable(),
  total: z.string().nullable(),
  price: z.string().nullable(),
  synthetic_code: z.string().nullable(),
});

export const dfeSubmissionSchema = z.object({
  id: z.number(),
  update_reason_code: z.string().nullable(),
  update_reason: z.string().nullable(),
  order_total_price: z.string().nullable(),
  created_at: z.string().nullable(),
  created_by: z.string().nullable(),
  status: z.string().nullable(),
  is_accept: z.boolean().nullable(),
  line_items: z.array(dfeLineItemSchema),
});

export const dfeResponseSchema = z.object({
  data: z.array(dfeSubmissionSchema),
});

/**
 * Body for creating a new DFE submission.
 *
 * Each line item carries the material identity (name + synthetic_code, optional)
 * along with the captured unit price and quantity. `total = price * quantity`
 * and the submission's `order_total_price` is the sum of all line-item totals.
 */
export const createDfeLineItemSchema = z.object({
  name: z.string().min(1).max(300),
  unit: z.string().max(5).optional().nullable(),
  price: z.number().nonnegative(),
  quantity: z.number().int().positive(),
  synthetic_code: z.string().max(50).optional().nullable(),
});

export const createDfeBodySchema = z.object({
  line_items: z.array(createDfeLineItemSchema).min(1),
  notes: z.string().max(2000).optional().nullable(),
});

export type DfeLineItem = z.infer<typeof dfeLineItemSchema>;
export type DfeSubmission = z.infer<typeof dfeSubmissionSchema>;
export type DfeResponse = z.infer<typeof dfeResponseSchema>;
export type CreateDfeLineItem = z.infer<typeof createDfeLineItemSchema>;
export type CreateDfeBody = z.infer<typeof createDfeBodySchema>;

/**
 * DFE API Contract
 *
 * Base path: /jobs/dfe  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const dfeContract = c.router({
  getDfe: {
    method: 'GET',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    responses: {
      200: dfeResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get DFE submissions for a job',
    description:
      'Returns all Departure From Estimate submissions with line items for a specific job.',
  },

  createDfe: {
    method: 'POST',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: createDfeBodySchema,
    responses: {
      200: dfeSubmissionSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new DFE submission',
    description:
      'Creates a bt_dfe row with one or more bt_dfe_product_items line items for the job.',
  },
});
